import moment from "moment";

const EXPIRY_ELIGIBLE_STATUSES = new Set(["-1", -1]);

const normalizeTestName = (test) => (
  typeof test === "string" ? test : test?.Test
);

const getSampleTestNames = (sample) => {
  if (!sample) return [];
  const tests = (Array.isArray(sample.tests_list) && sample.tests_list.length)
    ? sample.tests_list
    : (sample.test_list ?? []);
  return tests.map(normalizeTestName).filter(Boolean);
};

/** Collect unique test names from a submission (list or detail response). */
export const getSubmissionTestsList = (submission) => {
  if (!submission) return [];

  const directLists = [
    submission.tests_list,
    submission.test_list,
    submission.tests_available,
  ];
  const directNames = directLists
    .flatMap((list) => (Array.isArray(list) ? list : []))
    .map(normalizeTestName)
    .filter(Boolean);
  if (directNames.length) {
    return [...new Set(directNames)];
  }

  const samples = submission.samples_list ?? submission.samples ?? [];
  if (!Array.isArray(samples)) return [];

  return [...new Set(samples.flatMap(getSampleTestNames))];
};

/** All test names from every sample in order — for expiry modal header. */
export const getAllSubmissionTestsForHeader = (submission) => {
  const samples = submission?.samples_list ?? submission?.samples ?? [];
  if (!Array.isArray(samples) || !samples.length) return [];
  return samples.flatMap(getSampleTestNames);
};

export const formatSubmissionTestsForHeader = (submission) => {
  const tests = getAllSubmissionTestsForHeader(submission);
  return tests.length ? tests.join(", ") : "-";
};

export const hasSubmissionExpiry = (submission) => (
  EXPIRY_ELIGIBLE_STATUSES.has(submission?.status)
  && Boolean(submission?.expiry_data?.warning_flag)
);

export const hasSubmissionTrackingInfo = (submission) => (
  Boolean(submission?.tracking_info)
);

export const TRACKING_INFO_TOOLTIP = "Action has been taken and a team is working in the background.";

const TRACKING_INFO_FIELD_LABELS = {
  courier: "Courier",
  select_date: "Shipment Date",
  shipment_date: "Shipment Date",
  tracking_numbers: "Tracking Number",
  tracking_number: "Tracking Number",
  comments: "Comments",
  comment: "Comments",
  action_by: "Action By",
};

const TRACKING_INFO_FIELD_ORDER = [
  "courier",
  "select_date",
  "shipment_date",
  "tracking_numbers",
  "tracking_number",
  "comments",
  "comment",
  "action_by",
];

const formatTrackingInfoLabel = (key) => (
  TRACKING_INFO_FIELD_LABELS[key]
  || key.replace(/_/g, " ").replace(/\b\w/g, (char) => char.toUpperCase())
);

const formatTrackingInfoValue = (key, value) => {
  const text = String(value ?? "").trim();
  if (!text) return "";
  if (key.includes("date")) {
    const formatted = moment(text, [moment.ISO_8601, "YYYY-MM-DD", "DD-MM-YYYY"], true);
    if (formatted.isValid()) return formatted.format("D MMM YYYY");
  }
  return text;
};

/** Key-value rows for Action Taken tooltip from submission.tracking_info. */
export const getTrackingInfoTooltipRows = (submission) => {
  const trackingInfo = submission?.tracking_info;
  if (!trackingInfo || typeof trackingInfo !== "object") return [];

  const entries = Object.entries(trackingInfo).filter(([, value]) => {
    if (value === null || value === undefined) return false;
    if (typeof value === "object") return false;
    return String(value).trim() !== "";
  });

  entries.sort(([keyA], [keyB]) => {
    const indexA = TRACKING_INFO_FIELD_ORDER.indexOf(keyA);
    const indexB = TRACKING_INFO_FIELD_ORDER.indexOf(keyB);
    if (indexA === -1 && indexB === -1) return keyA.localeCompare(keyB);
    if (indexA === -1) return 1;
    if (indexB === -1) return -1;
    return indexA - indexB;
  });

  return entries.map(([key, value]) => ({
    label: formatTrackingInfoLabel(key),
    value: formatTrackingInfoValue(key, value),
  }));
};

/**
 * Calculation uses full date+time (UTC → local) so FE aligns with BE timestamp logic.
 * Display elsewhere remains days-only (no hours shown in the UI).
 */
export const getExpiryDaysUntil = (expirationDateUtc, now = moment()) => {
  const expirationMoment = moment.utc(expirationDateUtc).local();
  return Math.max(0, expirationMoment.diff(now, "days"));
};

export const getSubmissionExpiryDisplay = (submission) => {
  if (!hasSubmissionExpiry(submission)) return null;

  const expirationDateUtc = submission?.expiry_data?.expiration_date;
  if (!expirationDateUtc) {
    return {
      desc: "Expiry warning",
      date: "",
      tooltipTitle: "Expiry warning",
      tooltipBody: "This submission will move to Draft if no action is taken.",
    };
  }

  const expirationMoment = moment.utc(expirationDateUtc).local();
  const daysUntil = getExpiryDaysUntil(expirationDateUtc);
  const formattedDate = expirationMoment.format("D MMM YYYY");
  const dayWord = daysUntil === 1 ? "day" : "days";
  const dayWordTitle = daysUntil === 1 ? "Day" : "Days";

  return {
    desc: `Expiry in ${daysUntil} ${dayWord}`,
    date: formattedDate,
    tooltipTitle: `Expiry in ${daysUntil} ${dayWordTitle}`,
    tooltipBody: `This submission will move to Draft on ${formattedDate} if no action is taken.`,
  };
};
